import React, { useState, useRef, useEffect } from 'react';
import Icon from '../AppIcon';
import Input from './Input';
import { MAJOR_CITIES, US_STATES } from '../../constants';
import { useDebounce } from '../../hooks/useDebounce';
import { useGeolocation } from '../../hooks/useGeolocation';

const LocationInput = ({ 
  value = '', 
  onChange, 
  onLocationSelect, 
  placeholder = "Enter city, state, or ZIP", 
  variant = 'default',
  className = '' 
}) => {
  const [locationQuery, setLocationQuery] = useState(value);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [showLocationDetected, setShowLocationDetected] = useState(false);
  const locationRef = useRef(null);
  const suggestionsRef = useRef(null);
  const { location: userLocation, loading: locationLoading, error: locationError } = useGeolocation();
  
  const debouncedQuery = useDebounce(locationQuery, 300);

  // Show location detected notification when location is available
  useEffect(() => {
    if (userLocation && !locationLoading && !locationError) {
      setShowLocationDetected(true);
      // Hide after 4 seconds
      const timer = setTimeout(() => {
        setShowLocationDetected(false);
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [userLocation, locationLoading, locationError]);

  // Enhanced ZIP codes with proper formatting for major cities
  const ZIP_CODES = [
    { zip: '10001', city: 'New York', state: 'NY', displayText: 'New York, NY (10001)' },
    { zip: '10002', city: 'New York', state: 'NY', displayText: 'New York, NY (10002)' },
    { zip: '10003', city: 'New York', state: 'NY', displayText: 'New York, NY (10003)' },
    { zip: '10004', city: 'New York', state: 'NY', displayText: 'New York, NY (10004)' },
    { zip: '10005', city: 'New York', state: 'NY', displayText: 'New York, NY (10005)' },
    { zip: '90210', city: 'Beverly Hills', state: 'CA', displayText: 'Beverly Hills, CA (90210)' },
    { zip: '90211', city: 'Beverly Hills', state: 'CA', displayText: 'Beverly Hills, CA (90211)' },
    { zip: '90212', city: 'Beverly Hills', state: 'CA', displayText: 'Beverly Hills, CA (90212)' },
    { zip: '60601', city: 'Chicago', state: 'IL', displayText: 'Chicago, IL (60601)' },
    { zip: '60602', city: 'Chicago', state: 'IL', displayText: 'Chicago, IL (60602)' },
    { zip: '60603', city: 'Chicago', state: 'IL', displayText: 'Chicago, IL (60603)' },
    { zip: '60604', city: 'Chicago', state: 'IL', displayText: 'Chicago, IL (60604)' },
    { zip: '77001', city: 'Houston', state: 'TX', displayText: 'Houston, TX (77001)' },
    { zip: '77002', city: 'Houston', state: 'TX', displayText: 'Houston, TX (77002)' },
    { zip: '77003', city: 'Houston', state: 'TX', displayText: 'Houston, TX (77003)' },
    { zip: '77004', city: 'Houston', state: 'TX', displayText: 'Houston, TX (77004)' },
    { zip: '85001', city: 'Phoenix', state: 'AZ', displayText: 'Phoenix, AZ (85001)' },
    { zip: '85002', city: 'Phoenix', state: 'AZ', displayText: 'Phoenix, AZ (85002)' },
    { zip: '85003', city: 'Phoenix', state: 'AZ', displayText: 'Phoenix, AZ (85003)' },
    { zip: '19101', city: 'Philadelphia', state: 'PA', displayText: 'Philadelphia, PA (19101)' },
    { zip: '19102', city: 'Philadelphia', state: 'PA', displayText: 'Philadelphia, PA (19102)' },
    { zip: '19103', city: 'Philadelphia', state: 'PA', displayText: 'Philadelphia, PA (19103)' },
    { zip: '78201', city: 'San Antonio', state: 'TX', displayText: 'San Antonio, TX (78201)' },
    { zip: '78202', city: 'San Antonio', state: 'TX', displayText: 'San Antonio, TX (78202)' },
    { zip: '78203', city: 'San Antonio', state: 'TX', displayText: 'San Antonio, TX (78203)' },
    { zip: '92101', city: 'San Diego', state: 'CA', displayText: 'San Diego, CA (92101)' },
    { zip: '92102', city: 'San Diego', state: 'CA', displayText: 'San Diego, CA (92102)' },
    { zip: '92103', city: 'San Diego', state: 'CA', displayText: 'San Diego, CA (92103)' },
    { zip: '75201', city: 'Dallas', state: 'TX', displayText: 'Dallas, TX (75201)' },
    { zip: '75202', city: 'Dallas', state: 'TX', displayText: 'Dallas, TX (75202)' },
    { zip: '75203', city: 'Dallas', state: 'TX', displayText: 'Dallas, TX (75203)' },
    { zip: '95101', city: 'San Jose', state: 'CA', displayText: 'San Jose, CA (95101)' },
    { zip: '95102', city: 'San Jose', state: 'CA', displayText: 'San Jose, CA (95102)' },
    { zip: '95103', city: 'San Jose', state: 'CA', displayText: 'San Jose, CA (95103)' },
    { zip: '78701', city: 'Austin', state: 'TX', displayText: 'Austin, TX (78701)' },
    { zip: '78702', city: 'Austin', state: 'TX', displayText: 'Austin, TX (78702)' },
    { zip: '78703', city: 'Austin', state: 'TX', displayText: 'Austin, TX (78703)' },
    { zip: '32201', city: 'Jacksonville', state: 'FL', displayText: 'Jacksonville, FL (32201)' },
    { zip: '32202', city: 'Jacksonville', state: 'FL', displayText: 'Jacksonville, FL (32202)' },
    { zip: '32203', city: 'Jacksonville', state: 'FL', displayText: 'Jacksonville, FL (32203)' },
    { zip: '76101', city: 'Fort Worth', state: 'TX', displayText: 'Fort Worth, TX (76101)' },
    { zip: '76102', city: 'Fort Worth', state: 'TX', displayText: 'Fort Worth, TX (76102)' },
    { zip: '76103', city: 'Fort Worth', state: 'TX', displayText: 'Fort Worth, TX (76103)' },
    { zip: '43201', city: 'Columbus', state: 'OH', displayText: 'Columbus, OH (43201)' },
    { zip: '43202', city: 'Columbus', state: 'OH', displayText: 'Columbus, OH (43202)' },
    { zip: '43203', city: 'Columbus', state: 'OH', displayText: 'Columbus, OH (43203)' },
    { zip: '28201', city: 'Charlotte', state: 'NC', displayText: 'Charlotte, NC (28201)' },
    { zip: '28202', city: 'Charlotte', state: 'NC', displayText: 'Charlotte, NC (28202)' },
    { zip: '28203', city: 'Charlotte', state: 'NC', displayText: 'Charlotte, NC (28203)' },
    { zip: '94101', city: 'San Francisco', state: 'CA', displayText: 'San Francisco, CA (94101)' },
    { zip: '94102', city: 'San Francisco', state: 'CA', displayText: 'San Francisco, CA (94102)' },
    { zip: '94103', city: 'San Francisco', state: 'CA', displayText: 'San Francisco, CA (94103)' },
    { zip: '46201', city: 'Indianapolis', state: 'IN', displayText: 'Indianapolis, IN (46201)' },
    { zip: '46202', city: 'Indianapolis', state: 'IN', displayText: 'Indianapolis, IN (46202)' },
    { zip: '46203', city: 'Indianapolis', state: 'IN', displayText: 'Indianapolis, IN (46203)' },
    { zip: '98101', city: 'Seattle', state: 'WA', displayText: 'Seattle, WA (98101)' },
    { zip: '98102', city: 'Seattle', state: 'WA', displayText: 'Seattle, WA (98102)' },
    { zip: '98103', city: 'Seattle', state: 'WA', displayText: 'Seattle, WA (98103)' },
    { zip: '80201', city: 'Denver', state: 'CO', displayText: 'Denver, CO (80201)' },
    { zip: '80202', city: 'Denver', state: 'CO', displayText: 'Denver, CO (80202)' },
    { zip: '80203', city: 'Denver', state: 'CO', displayText: 'Denver, CO (80203)' },
    { zip: '20001', city: 'Washington', state: 'DC', displayText: 'Washington, DC (20001)' },
    { zip: '20002', city: 'Washington', state: 'DC', displayText: 'Washington, DC (20002)' },
    { zip: '20003', city: 'Washington', state: 'DC', displayText: 'Washington, DC (20003)' },
    { zip: '02101', city: 'Boston', state: 'MA', displayText: 'Boston, MA (02101)' },
    { zip: '02102', city: 'Boston', state: 'MA', displayText: 'Boston, MA (02102)' },
    { zip: '02103', city: 'Boston', state: 'MA', displayText: 'Boston, MA (02103)' },
    { zip: '30301', city: 'Atlanta', state: 'GA', displayText: 'Atlanta, GA (30301)' },
    { zip: '30302', city: 'Atlanta', state: 'GA', displayText: 'Atlanta, GA (30302)' },
    { zip: '30303', city: 'Atlanta', state: 'GA', displayText: 'Atlanta, GA (30303)' },
    { zip: '37201', city: 'Nashville', state: 'TN', displayText: 'Nashville, TN (37201)' },
    { zip: '37202', city: 'Nashville', state: 'TN', displayText: 'Nashville, TN (37202)' },
    { zip: '37203', city: 'Nashville', state: 'TN', displayText: 'Nashville, TN (37203)' },
    { zip: '48201', city: 'Detroit', state: 'MI', displayText: 'Detroit, MI (48201)' },
    { zip: '48202', city: 'Detroit', state: 'MI', displayText: 'Detroit, MI (48202)' },
    { zip: '48203', city: 'Detroit', state: 'MI', displayText: 'Detroit, MI (48203)' },
    { zip: '97201', city: 'Portland', state: 'OR', displayText: 'Portland, OR (97201)' },
    { zip: '97202', city: 'Portland', state: 'OR', displayText: 'Portland, OR (97202)' },
    { zip: '97203', city: 'Portland', state: 'OR', displayText: 'Portland, OR (97203)' },
    { zip: '89101', city: 'Las Vegas', state: 'NV', displayText: 'Las Vegas, NV (89101)' },
    { zip: '89102', city: 'Las Vegas', state: 'NV', displayText: 'Las Vegas, NV (89102)' },
    { zip: '89103', city: 'Las Vegas', state: 'NV', displayText: 'Las Vegas, NV (89103)' },
    { zip: '33101', city: 'Miami', state: 'FL', displayText: 'Miami, FL (33101)' },
    { zip: '33102', city: 'Miami', state: 'FL', displayText: 'Miami, FL (33102)' },
    { zip: '33103', city: 'Miami', state: 'FL', displayText: 'Miami, FL (33103)' },
    { zip: '33601', city: 'Tampa', state: 'FL', displayText: 'Tampa, FL (33601)' },
    { zip: '33602', city: 'Tampa', state: 'FL', displayText: 'Tampa, FL (33602)' },
    { zip: '33603', city: 'Tampa', state: 'FL', displayText: 'Tampa, FL (33603)' },
    { zip: '32801', city: 'Orlando', state: 'FL', displayText: 'Orlando, FL (32801)' },
    { zip: '32802', city: 'Orlando', state: 'FL', displayText: 'Orlando, FL (32802)' },
    { zip: '32803', city: 'Orlando', state: 'FL', displayText: 'Orlando, FL (32803)' }
  ];

  const generateLocationSuggestions = (query) => {
    if (!query || query.length < 2) return [];

    const suggestions = [];
    const lowercaseQuery = query.toLowerCase();

    // Check if query is a ZIP code pattern
    const isZipQuery = /^\d{1,5}$/.test(query);
    
    if (isZipQuery) {
      // Search ZIP codes with proper formatting
      const zipResults = ZIP_CODES.filter(item => 
        item.zip.startsWith(query)
      ).slice(0, 8);
      
      suggestions.push(...zipResults.map(item => ({
        type: 'zip',
        label: item.displayText,
        value: item.zip,
        city: item.city,
        state: item.state,
        displayText: item.displayText
      })));
    } else {
      // Search cities and format as City, State
      const cityResults = MAJOR_CITIES.filter(cityState =>
        cityState.toLowerCase().includes(lowercaseQuery)
      ).slice(0, 6);
      
      suggestions.push(...cityResults.map(cityState => ({
        type: 'city',
        label: cityState,
        value: cityState,
        displayText: cityState
      })));

      // Search states and format as State Name (CODE)
      const stateResults = Object.entries(US_STATES).filter(([code, name]) =>
        name.toLowerCase().includes(lowercaseQuery) || 
        code.toLowerCase().includes(lowercaseQuery)
      ).slice(0, 4);
      
      suggestions.push(...stateResults.map(([code, name]) => ({
        type: 'state',
        label: `${name} (${code})`,
        value: code,
        displayText: `${name} (${code})`
      })));

      // Add matching ZIP codes for partial city matches
      const partialZipResults = ZIP_CODES.filter(item => 
        item.city.toLowerCase().includes(lowercaseQuery) ||
        item.state.toLowerCase().includes(lowercaseQuery)
      ).slice(0, 4);
      
      suggestions.push(...partialZipResults.map(item => ({
        type: 'zip',
        label: item.displayText,
        value: item.zip,
        city: item.city,
        state: item.state,
        displayText: item.displayText
      })));
    }

    return suggestions.slice(0, 10);
  };

  useEffect(() => {
    if (debouncedQuery.length >= 2) {
      const newSuggestions = generateLocationSuggestions(debouncedQuery);
      setSuggestions(newSuggestions);
      setShowSuggestions(true);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
    setSelectedIndex(-1);
  }, [debouncedQuery]);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setLocationQuery(value);
    onChange?.(value);
  };

  const handleSuggestionClick = (suggestion) => {
    setLocationQuery(suggestion.displayText);
    setShowSuggestions(false);
    setSelectedIndex(-1);
    onChange?.(suggestion.displayText);
    onLocationSelect?.(suggestion);
  };

  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0) {
          handleSuggestionClick(suggestions[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setSelectedIndex(-1);
        break;
    }
  };

  const handleBlur = (e) => {
    // Delay hiding suggestions to allow clicking
    setTimeout(() => {
      if (!suggestionsRef.current?.contains(e.relatedTarget)) {
        setShowSuggestions(false);
        setSelectedIndex(-1);
      }
    }, 150);
  };

  const handleUseCurrentLocation = () => {
    if (userLocation) {
      // In a real app, you would reverse geocode the coordinates
      setLocationQuery('Current Location');
      setShowSuggestions(false);
      onChange?.('Current Location');
      onLocationSelect?.({
        type: 'current',
        label: 'Current Location',
        value: 'current',
        coordinates: userLocation,
        displayText: 'Current Location'
      });
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (locationRef.current && !locationRef.current.contains(event.target)) {
        setShowSuggestions(false);
        setSelectedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getInputClassName = () => {
    const baseClass = "border-none bg-transparent focus:ring-0 focus:outline-none";
    
    if (variant === 'hero-desktop') {
      return `${baseClass} text-lg placeholder:text-text-muted py-4 ${className}`;
    } else if (variant === 'hero-mobile') {
      return `${baseClass} text-base placeholder:text-text-muted py-3 ${className}`;
    }
    
    return `${baseClass} placeholder:text-text-muted py-3 ${className}`;
  };

  const getContainerClassName = () => {
    if (variant === 'hero-desktop' || variant === 'hero-mobile') {
      return 'relative flex-1';
    }
    return 'relative w-full';
  };

  return (
    <div className={getContainerClassName()} ref={locationRef}>
      {/* Location Status Notification */}
      {locationLoading && (
        <div className="absolute -top-12 left-0 right-0 bg-blue-50 border border-blue-200 text-blue-700 px-4 py-2 rounded-lg shadow-medium flex items-center space-x-2 z-50 animate-slide-down">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
          <span className="text-sm font-medium">Requesting location access...</span>
        </div>
      )}
      
      {showLocationDetected && userLocation && (
        <div className="absolute -top-12 left-0 right-0 bg-green-50 border border-green-200 text-green-700 px-4 py-2 rounded-lg shadow-medium flex items-center space-x-2 z-50 animate-slide-down">
          <Icon name="CheckCircle" size={16} color="currentColor" />
          <span className="text-sm font-medium">Location detected! Click the crosshair to use it.</span>
        </div>
      )}
      
      {locationError && (
        <div className="absolute -top-12 left-0 right-0 bg-yellow-50 border border-yellow-200 text-yellow-700 px-4 py-2 rounded-lg shadow-medium flex items-center space-x-2 z-50 animate-slide-down">
          <Icon name="AlertCircle" size={16} color="currentColor" />
          <span className="text-sm font-medium">Location access denied. Enter manually.</span>
        </div>
      )}

      <div className="relative flex items-center">
        <div className="flex items-center pl-4 pr-2">
          <Icon name="MapPin" size={variant === 'hero-mobile' ? 18 : 20} color="#667eea" />
        </div>
        <Input
          type="text"
          value={locationQuery}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          placeholder={placeholder}
          className={getInputClassName()}
          autoComplete="off"
        />
        {locationLoading && (
          <div className="flex items-center justify-center w-8 h-8 mr-2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
          </div>
        )}
        {userLocation && !locationLoading && (
          <button
            type="button"
            onClick={handleUseCurrentLocation}
            className="flex items-center justify-center w-8 h-8 text-green-600 hover:text-green-700 hover:bg-green-50 rounded-lg transition-smooth mr-2 pulse-once"
            title="Use current location"
          >
            <Icon name="Crosshair" size={16} color="currentColor" />
          </button>
        )}
      </div>

      {/* Enhanced Suggestions Dropdown */}
      {showSuggestions && suggestions.length > 0 && (
        <div 
          ref={suggestionsRef}
          className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-strong border border-border-light z-50 overflow-hidden animate-slide-down"
        >
          <div className="p-2 max-h-80 overflow-y-auto">
            {suggestions.map((suggestion, index) => (
              <button
                key={index}
                onClick={() => handleSuggestionClick(suggestion)}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-smooth text-left ${
                  index === selectedIndex 
                    ? 'bg-primary-50 text-primary border-primary border' :'hover:bg-surface-100'
                }`}
              >
                <div className={`flex items-center justify-center w-8 h-8 rounded-lg ${
                  suggestion.type === 'zip' ? 'bg-accent-50 text-accent' 
                    : suggestion.type === 'city'? 'bg-primary-50 text-primary' :'bg-secondary-50 text-secondary'
                }`}>
                  <Icon 
                    name={suggestion.type === 'zip' ? 'Hash' : suggestion.type === 'city' ? 'Building' : 'Map'} 
                    size={16} 
                    color="currentColor" 
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-text-primary truncate">{suggestion.label}</div>
                  <div className="text-sm text-text-secondary font-caption">
                    {suggestion.type === 'zip' ? 'ZIP Code' : 
                     suggestion.type === 'city' ? 'City' : 'State'}
                  </div>
                </div>
                <Icon name="ArrowUpRight" size={16} color="#667eea" />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default LocationInput;